import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal
} from "@angular/core";
import { ReportDesignItemSignatureValue } from "../../report-design-item";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { HealthRecord } from "src/app/backend/models/health-record";
import {
  loadSignatureHtmlListForConfig,
  parseSignatureAuthorMemberIdsKey
} from "src/app/modules/bootstrap/modules/report/report-signature-support";

@Component({
  selector: "snt-report-form-renderer-signature",
  templateUrl: "report-form-renderer-signature.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class ReportFormRendererSignatureComponent implements OnChanges {
  @Input({ required: true })
  item!: ReportDesignItemSignatureValue;

  @Input({ required: true })
  healthRecord!: HealthRecord | undefined;

  @Input()
  dataSource?: Record<string, unknown>;

  @Input()
  authorMemberIdsKey = "";

  @Input()
  level!: number;

  @Input()
  parentName!: string;

  @Output()
  value: EventEmitter<void> = new EventEmitter();

  align = signal<"left" | "center" | "right" | undefined>(undefined);
  signatureHtml = signal<SafeHtml[] | undefined>(undefined);

  private readonly memberBackend = inject(MemberBackend);
  private readonly userBackend = inject(UserBackend);
  private readonly domSanitizer = inject(DomSanitizer);

  private loadSignaturesGeneration = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["item"] ||
      changes["healthRecord"] ||
      changes["dataSource"] ||
      changes["authorMemberIdsKey"]
    ) {
      const generation = ++this.loadSignaturesGeneration;

      void this.loadSignatures(generation);
    }
  }

  private async loadSignatures(generation: number): Promise<void> {
    this.align.set(
      <"left" | "center" | "right" | undefined>this.item.config?.align
    );

    const isAuthorsMode = this.item.config?.user === "authors";
    const authorIds = isAuthorsMode
      ? parseSignatureAuthorMemberIdsKey(this.authorMemberIdsKey)
      : [];

    const signatures = await loadSignatureHtmlListForConfig({
      userConfig: this.item.config?.user,
      memberBackend: this.memberBackend,
      userBackend: this.userBackend,
      healthRecord: this.healthRecord,
      dataSource:
        isAuthorsMode && authorIds.length > 0
          ? { ...this.dataSource, authors: authorIds }
          : this.dataSource
    });

    if (generation !== this.loadSignaturesGeneration) {
      return;
    }

    this.signatureHtml.set(
      signatures.map((signature) =>
        this.domSanitizer.bypassSecurityTrustHtml(signature)
      )
    );
  }

  async renderPdfObject(): Promise<Record<string, unknown>> {
    return {
      type: "signature",
      config: {
        signature: "",
        value: ""
      }
    };
  }
}
